<script setup lang="ts">
import { computed, inject } from 'vue'
import { Doughnut } from 'vue-chartjs'
import { ArcElement, Chart as ChartJS, Legend, Tooltip, } from 'chart.js'
import { useLocalization } from '@hantera/portal-app'
import { Badge } from '@hantera/design-system'
import type { useJobDefinitionDetailsStore } from '@/stores/jobs/job-definition-details-store'
import Decimal from 'decimal.js'

ChartJS.register(ArcElement, Tooltip, Legend)

const t = useLocalization('jobs')
const store = inject<ReturnType<typeof useJobDefinitionDetailsStore>>('jobDefinitionStore')!

const chartData = computed(() => {
  const successful = store.summary?.successful ?? new Decimal(0)
  const failed = store.summary?.failed ?? new Decimal(0)

  return {
    labels: [t('detail.summary.successful'), t('detail.summary.failed')],
    datasets: [
      {
        data: [successful.toNumber(), failed.toNumber()],
        backgroundColor: ['hsl(142, 71%, 45%)', 'hsl(0, 84%, 60%)'],
        borderWidth: 0,
      },
    ],
  }
})

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom' as const,
    },
  },
}

const formatMs = (value: Decimal | null | undefined) => {
  if (!value) return '-'
  const num = value.toNumber()
  if (num < 1000) return `${num.toFixed(0)} ms`
  if (num < 60000) return `${(num / 1000).toFixed(1)} s`
  return `${(num / 60000).toFixed(1)} min`
}
</script>

<template>
  <div class="summary">

    <div class="stats-card box-content">
      <div class="card-header">
        <h3>{{ t('detail.summary.jobResults') }}</h3>
        <span class="time-range">{{ t('detail.summary.last24Hours') }}</span>
      </div>
      <div class="chart-container" v-if="store.summary">
        <Doughnut :data="chartData" :options="chartOptions" />
      </div>
      <div class="no-data" v-else>
        {{ t('detail.summary.noData') }}
      </div>
    </div>

    <div class="stats-card box-content">
      <div class="card-header">
        <h3>{{ t('detail.summary.executionStats') }}</h3>
        <span class="time-range">{{ t('detail.summary.last24Hours') }}</span>
      </div>
      <div class="stats-grid" v-if="store.summary">
        <div class="stat">
          <span class="label">{{ t('detail.summary.totalJobs') }}</span>
          <span class="value">{{ store.summary.total.toString() }}</span>
        </div>
        <div class="stat">
          <span class="label">{{ t('detail.summary.avgTime') }}</span>
          <span class="value">{{ formatMs(store.summary.avgExecution) }}</span>
        </div>
        <div class="stat">
          <span class="label">{{ t('detail.summary.minTime') }}</span>
          <span class="value">{{ formatMs(store.summary.minExecution) }}</span>
        </div>
        <div class="stat">
          <span class="label">{{ t('detail.summary.maxTime') }}</span>
          <span class="value">{{ formatMs(store.summary.maxExecution) }}</span>
        </div>
        <div class="stat">
          <span class="label">{{ t('detail.summary.successRate') }}</span>
          <span class="value success-rate">{{ store.summary.successRate.toFixed(1) }}%</span>
        </div>
      </div>
      <div class="no-data" v-else>
        {{ t('detail.summary.noData') }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.summary {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.5rem;
}

.schedule-badge-row {
  display: flex;
  justify-content: flex-end;
}

.stats-card {
  padding: 1rem;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 1rem;
}

.stats-card h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: hsl(var(--color-30));
}

.time-range {
  font-size: 11px;
  color: hsl(var(--color-50));
  font-weight: 400;
}

.chart-container {
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stats-grid {
  display: grid;
  gap: 0.75rem;
}

.stat {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stat .label {
  font-size: 13px;
  color: hsl(var(--color-40));
}

.stat .value {
  font-size: 14px;
  font-weight: 500;
  color: hsl(var(--color-20));
}

.success-rate {
  color: hsl(142, 71%, 35%);
}

.no-data {
  color: hsl(var(--color-50));
  font-size: 13px;
  text-align: center;
  padding: 2rem;
}
</style>
